import { type Block, type Change, generateKey, Schema } from '@gitbook/slate';

import {
    getConflictCounterpartKey,
    getConflictKeyOrigin,
    getOriginalKey,
    hasConflictData,
    isConflictKey,
} from '../conflicts';

/**
 * Schema when editing a document with conflicts
 */
export const schemaForConflicts = Schema.create({
    validations: [
        /*
         * Rule to set the schemaType to "conflicts" on the document.
         */
        function noConflictSchemaType(node) {
            if (node.object !== 'document' || node.data.get('schemaType') === 'conflicts') {
                return null;
            }

            return (change) => {
                change.setDataByKey(node.key, ['schemaType'], 'conflicts');
            };
        },
        /*
         * Rule to ensure that a conflict has its alternatives in the document.
         * Otherwise the block is marked as "normal".
         */
        function noInvalidConflicts(node) {
            // Conflicts are being managed only at the top level
            if (node.object !== 'document') {
                return null;
            }

            const missingConflictKey: Block[] = [];
            const missingConflictData: Block[] = [];
            const missingCounterpart: Block[] = [];
            node.nodes.forEach((child) => {
                if (!isConflictKey(child.key) && !hasConflictData(child)) {
                    // Not a trace of conflict
                    return;
                }

                if (!isConflictKey(child.key)) {
                    missingConflictKey.push(child);
                    return;
                }

                if (!hasConflictData(child)) {
                    missingConflictData.push(child);
                    return;
                }

                const counterpartKey = getConflictCounterpartKey(child.key);
                const hasCounterpart = node.nodes.find((other) => other.key === counterpartKey);
                if (!hasCounterpart) {
                    missingCounterpart.push(child);
                }
            });

            if (
                missingConflictKey.length === 0 &&
                missingConflictData.length === 0 &&
                missingCounterpart.length === 0
            ) {
                return null;
            }

            return (change) => {
                missingConflictKey.forEach((n) => {
                    change.setNodeByKey(
                        n.key,
                        {
                            data: n.data.delete('conflict'),
                        },
                        { normalize: false }
                    );
                });
                missingConflictData.forEach((n) => {
                    change.removeNodeByKey(n.key, { normalize: false });
                });
                missingCounterpart.forEach((n) => {
                    cleanConflict(change, n);
                });
            };
        },
    ],
});

/**
 * Schema to remove conflicts data.
 */
export const schemaToStripConflicts = Schema.create({
    validations: [
        /*
         * Rule to remove the schemaType "conflicts" from the document.
         * This data is set during the mergeDocuments.
         */
        function noConflictSchemaType(node) {
            if (node.object !== 'document' || node.data.get('schemaType') !== 'conflicts') {
                return null;
            }

            return (change) => {
                change.removeDataByKey(node.key, ['schemaType']);
            };
        },
        /*
         * Rule to ensure that there are no conflicts in normal mode.
         * It removes everything related to base and cleanup keys for head nodes.
         */
        function noConflicts(node) {
            if (!isConflictKey(node.key)) {
                return null;
            }

            return (change) => {
                const origin = getConflictKeyOrigin(node.key);

                if (origin === 'base') {
                    change.removeNodeByKey(node.key);
                    return;
                }

                const cleanedNode = node.merge({
                    key: getOriginalKey(node.key),
                    ...('data' in node && node.data
                        ? {
                              data: node.data.delete('conflict'),
                          }
                        : {}),
                });

                // We need to use replaceNodeByKey in order to change a block key
                return change.replaceNodeByKey(node.key, cleanedNode);
            };
        },
    ],
});

//Tables are more complex structures where due to the number of fragments, it can be very costly to traverse them all
const BLOCK_TYPES_WITH_FRAGMENTS_IGNORED = ['table'];

/**
 * Clean conflict on a block and reset it to normal state (including all its descendant).
 */
function cleanConflict(change: Change, block: Block) {
    const cleanedBlock = block.merge({
        key: getOriginalKey(block.key),
        data: block.data.delete('conflict'),
    });

    // Update the selection if needed
    const { selection } = change.value;
    const newSelection = selection.merge({
        // @ts-expect-error
        anchorKey: selection.anchorKey === block.key ? cleanedBlock.key : selection.anchorKey,
        // @ts-expect-error
        focusKey: selection.focusKey === block.key ? cleanedBlock.key : selection.focusKey,
    });

    change.replaceNodeByKey(block.key, cleanedBlock, { normalize: false });

    block.forEachDescendant(
        (node) => {
            if (!isConflictKey(node.key)) {
                return;
            }

            // Node can have been deleted while deleting an ancestor
            if (!change.value.document.hasNode(node.key)) {
                return;
            }

            const originalKey = getOriginalKey(node.key);
            const existingNode = change.value.document.getNode(originalKey);

            change.replaceNodeByKey(
                node.key,
                node.merge({
                    key: existingNode ? generateKey() : originalKey,
                }),
                { normalize: false }
            );
        },
        { withFragments: !BLOCK_TYPES_WITH_FRAGMENTS_IGNORED.includes(block.type) }
    );

    change.normalizeNodeByKey(cleanedBlock.key);
    change.select(newSelection);
}
